import { useState } from 'react';
import { Plus, Search, Mail, ArrowLeft, Copy, Eye, X } from 'lucide-react';
import {
  useEmailTemplates,
  useCreateTemplate,
  useUpdateTemplate,
  useDeleteTemplate,
  usePreviewTemplate,
  type EmailTemplate,
} from '../hooks/useEmailTemplates';
import { Badge } from '../components/shared/Badge';
import { EmptyState } from '../components/shared/EmptyState';
import { cn } from '../lib/cn';

interface EmailTemplatesViewProps {
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const categoryOptions = [
  'Welcome',
  'Follow-up',
  'Renewal',
  'Claims',
  'Quote',
  'General',
  'Internal',
];

const categoryColors: Record<string, string> = {
  Welcome: 'green',
  'Follow-up': 'blue',
  Renewal: 'purple',
  Claims: 'red',
  Quote: 'orange',
  General: 'gray',
  Internal: 'slate',
};

type ViewMode = 'list' | 'form' | 'preview';

function extractVariables(body: string): string[] {
  const matches = body.match(/\{\{(\w+)\}\}/g) ?? [];
  return [...new Set(matches.map((m) => m.replace(/[{}]/g, '')))];
}

// --- Template Form ---

interface TemplateFormProps {
  initial?: EmailTemplate;
  onSave: (data: Partial<EmailTemplate>) => void;
  onCancel: () => void;
  isSaving: boolean;
}

function TemplateForm({ initial, onSave, onCancel, isSaving }: TemplateFormProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [subject, setSubject] = useState(initial?.subject ?? '');
  const [category, setCategory] = useState(initial?.category ?? 'General');
  const [body, setBody] = useState(initial?.body ?? '');

  const variables = extractVariables(body);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave({ name, subject, category, body, variables });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6">
      <div className="card p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-900">Template Details</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Name</label>
            <input
              type="text"
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Template name"
              required
            />
          </div>
          <div>
            <label className="label">Category</label>
            <select className="input" value={category} onChange={(e) => setCategory(e.target.value)}>
              {categoryOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="label">Subject</label>
          <input
            type="text"
            className="input"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject line (supports {{variables}})"
            required
          />
        </div>
        <div>
          <label className="label">Body</label>
          <textarea
            className="input font-mono text-sm"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Email body content. Use {{variableName}} for dynamic values."
            rows={12}
            required
          />
        </div>

        {variables.length > 0 && (
          <div>
            <label className="label">Detected Variables</label>
            <div className="flex flex-wrap gap-1.5">
              {variables.map((v) => (
                <Badge key={v} color="blue" size="sm">
                  {`{{${v}}}`}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-3">
        <button type="button" onClick={onCancel} className="btn-secondary">
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary"
          disabled={isSaving || !name.trim() || !subject.trim() || !body.trim()}
        >
          {isSaving ? 'Saving...' : initial ? 'Update Template' : 'Create Template'}
        </button>
      </div>
    </form>
  );
}

// --- Preview Panel ---

interface PreviewPanelProps {
  template: EmailTemplate;
  onBack: () => void;
}

function PreviewPanel({ template, onBack }: PreviewPanelProps) {
  const variables = template.variables ?? extractVariables(template.body);
  const [sampleData, setSampleData] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const v of variables) {
      initial[v] = `[${v}]`;
    }
    return initial;
  });

  function renderTemplate(text: string): string {
    return text.replace(/\{\{(\w+)\}\}/g, (_, key) => sampleData[key] ?? `{{${key}}}`);
  }

  return (
    <div className="p-6">
      <button
        onClick={onBack}
        className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to templates
      </button>

      <h2 className="text-lg font-semibold text-slate-900 mb-4">Preview: {template.name}</h2>

      {variables.length > 0 && (
        <div className="card p-4 mb-4 space-y-3">
          <h4 className="text-xs font-semibold text-slate-500 uppercase">Sample Data</h4>
          <div className="grid grid-cols-2 gap-3">
            {variables.map((v) => (
              <div key={v}>
                <label className="label">{`{{${v}}}`}</label>
                <input
                  type="text"
                  className="input"
                  value={sampleData[v] ?? ''}
                  onChange={(e) => setSampleData((d) => ({ ...d, [v]: e.target.value }))}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card p-5 space-y-3">
        <div>
          <span className="text-xs font-semibold text-slate-400 uppercase">Subject</span>
          <p className="text-sm font-medium text-slate-900">{renderTemplate(template.subject)}</p>
        </div>
        <hr className="border-surface-200" />
        <div>
          <span className="text-xs font-semibold text-slate-400 uppercase">Body</span>
          <div className="mt-2 whitespace-pre-wrap text-sm text-slate-700">
            {renderTemplate(template.body)}
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Main View ---

export function EmailTemplatesView({ onNavigate }: EmailTemplatesViewProps) {
  const [mode, setMode] = useState<ViewMode>('list');
  const [searchText, setSearchText] = useState('');
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | undefined>();
  const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | undefined>();

  const { data, isLoading, error } = useEmailTemplates();
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();
  const deleteTemplate = useDeleteTemplate();

  function handleCreate() {
    setEditingTemplate(undefined);
    setMode('form');
  }

  function handleEdit(template: EmailTemplate) {
    setEditingTemplate(template);
    setMode('form');
  }

  function handleClone(template: EmailTemplate) {
    setEditingTemplate({
      ...template,
      id: '',
      name: `${template.name} (Copy)`,
      isDefault: false,
    });
    setMode('form');
  }

  function handlePreview(template: EmailTemplate) {
    setPreviewTemplate(template);
    setMode('preview');
  }

  function handleSave(formData: Partial<EmailTemplate>) {
    if (editingTemplate?.id) {
      updateTemplate.mutate(
        { id: editingTemplate.id, data: formData },
        { onSuccess: () => setMode('list') },
      );
    } else {
      createTemplate.mutate(formData, { onSuccess: () => setMode('list') });
    }
  }

  const templates = data?.templates ?? [];
  const filtered = searchText
    ? templates.filter(
        (t) =>
          t.name.toLowerCase().includes(searchText.toLowerCase()) ||
          t.category.toLowerCase().includes(searchText.toLowerCase()),
      )
    : templates;

  const userTemplates = filtered.filter((t) => !t.isDefault);
  const defaultTemplates = filtered.filter((t) => t.isDefault);

  // --- Preview Mode ---
  if (mode === 'preview' && previewTemplate) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="page-header">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Email Templates</h1>
            <p className="mt-0.5 text-sm text-slate-500">Template preview</p>
          </div>
        </div>
        <PreviewPanel template={previewTemplate} onBack={() => setMode('list')} />
      </div>
    );
  }

  // --- Form Mode ---
  if (mode === 'form') {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="page-header">
          <div>
            <h1 className="text-xl font-bold text-slate-900">
              {editingTemplate?.id ? 'Edit Template' : 'New Template'}
            </h1>
            <p className="mt-0.5 text-sm text-slate-500">
              {editingTemplate?.id
                ? 'Update the email template'
                : 'Create a new email template'}
            </p>
          </div>
        </div>
        <TemplateForm
          initial={editingTemplate}
          onSave={handleSave}
          onCancel={() => setMode('list')}
          isSaving={createTemplate.isPending || updateTemplate.isPending}
        />
      </div>
    );
  }

  // --- List Mode ---
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="page-header">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Email Templates</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {data ? `${data.total} template${data.total !== 1 ? 's' : ''}` : 'Manage email templates'}
          </p>
        </div>
        <button className="btn-primary" onClick={handleCreate}>
          <Plus className="h-4 w-4" />
          New Template
        </button>
      </div>

      {/* Search */}
      <div className="border-b border-surface-200 bg-white px-6 py-3">
        <form onSubmit={(e) => e.preventDefault()} className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search templates..."
            className="input pl-9"
          />
        </form>
      </div>

      {/* Content */}
      <div className="p-6 space-y-6">
        {error ? (
          <div className="card p-8 text-center text-sm text-red-600">
            Failed to load templates. Please try again.
          </div>
        ) : isLoading ? (
          <div className="card divide-y divide-surface-100">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-4">
                <div className="h-9 w-9 animate-pulse rounded-lg bg-surface-200" />
                <div className="flex-1">
                  <div className="h-4 w-48 animate-pulse rounded bg-surface-200" />
                  <div className="mt-1.5 h-3 w-32 animate-pulse rounded bg-surface-100" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <>
            {/* User Templates */}
            {userTemplates.length > 0 && (
              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Your Templates ({userTemplates.length})
                </h3>
                <div className="card divide-y divide-surface-100">
                  {userTemplates.map((template) => (
                    <TemplateRow
                      key={template.id}
                      template={template}
                      onEdit={handleEdit}
                      onPreview={handlePreview}
                      onClone={handleClone}
                      onDelete={(id) => {
                        if (confirm('Delete this template?')) deleteTemplate.mutate(id);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Default Templates */}
            {defaultTemplates.length > 0 && (
              <div>
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
                  Default Templates ({defaultTemplates.length})
                </h3>
                <div className="card divide-y divide-surface-100">
                  {defaultTemplates.map((template) => (
                    <TemplateRow
                      key={template.id}
                      template={template}
                      onEdit={handleEdit}
                      onPreview={handlePreview}
                      onClone={handleClone}
                      isDefault
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <EmptyState
            icon={Mail}
            title="No templates"
            description="Create your first email template to standardize communication."
            actionLabel="Create Template"
            onAction={handleCreate}
          />
        )}
      </div>
    </div>
  );
}

// --- Template Row ---

interface TemplateRowProps {
  template: EmailTemplate;
  onEdit: (t: EmailTemplate) => void;
  onPreview: (t: EmailTemplate) => void;
  onClone: (t: EmailTemplate) => void;
  onDelete?: (id: string) => void;
  isDefault?: boolean;
}

function TemplateRow({ template, onEdit, onPreview, onClone, onDelete, isDefault }: TemplateRowProps) {
  return (
    <div className="flex items-center gap-4 px-4 py-4 hover:bg-surface-50 transition-colors">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-100 text-brand-600">
        <Mail className="h-5 w-5" />
      </div>

      <div
        className="min-w-0 flex-1 cursor-pointer"
        onClick={() => !isDefault && onEdit(template)}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-900">{template.name}</span>
          <Badge color={categoryColors[template.category] ?? 'gray'} size="sm">
            {template.category}
          </Badge>
          {isDefault && (
            <Badge color="slate" size="sm">Default</Badge>
          )}
        </div>
        <div className="mt-0.5 text-xs text-slate-400">
          Last updated: {new Date(template.updatedAt).toLocaleDateString()}
        </div>
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={() => onPreview(template)}
          className="p-1.5 text-slate-400 hover:text-slate-600"
          title="Preview"
        >
          <Eye className="h-4 w-4" />
        </button>
        <button
          onClick={() => onClone(template)}
          className="p-1.5 text-slate-400 hover:text-slate-600"
          title="Clone"
        >
          <Copy className="h-4 w-4" />
        </button>
        {!isDefault && onDelete && (
          <button
            onClick={() => onDelete(template.id)}
            className="p-1.5 text-slate-400 hover:text-red-500"
            title="Delete"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
